import classNames from 'classnames';
import React, { useRef } from 'react';

const styles = require('./StatusOverrideGroup.module.scss');

export interface StatusOption<T extends string> {
  label: string;
  value: T;
}

export interface Props<T extends string> {
  baseValue: T;
  disabled?: boolean;
  label: string;
  onChange: (override: T | null) => void;
  options: StatusOption<T>[];
  override: T | null;
}

// Generic tri-state override control: a radiogroup of real buttons with
// roving tabindex and arrow-key movement. Selecting the platform's own value
// clears the override instead of storing a redundant one.
export default function StatusOverrideGroup<T extends string>({
  baseValue,
  disabled = false,
  label,
  onChange,
  options,
  override,
}: Props<T>) {
  const selected = override !== null ? override : baseValue;
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (value: T) => {
    onChange(value === baseValue ? null : value);
  };

  const onKeyDown = (event: { key: string; preventDefault: () => void }, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const next = (index + delta + options.length) % options.length;
    const target = buttonsRef.current[next];
    if (target) {
      target.focus();
      select(options[next].value);
    }
  };

  return (
    <div aria-label={label} className={styles['ui-status-override']} role="radiogroup">
      {options.map((option, index) => {
        const isSelected = option.value === selected;
        return (
          <button
            aria-checked={isSelected}
            className={classNames(styles['ui-status-override__option'], {
              [styles[`ui-status-override__option--${option.value}`]]: isSelected,
            })}
            disabled={disabled}
            key={option.value}
            onClick={() => select(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            ref={(element) => {
              buttonsRef.current[index] = element;
            }}
            role="radio"
            tabIndex={isSelected ? 0 : -1}
            type="button"
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
